import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../lib/api";
import { requestNotificationToken } from "../lib/firebase";

/**
 * Push-notification opt-in for this device. Lives in the drawer's menu list
 * and on the Profile page (the drawer footer was unreachable under the phone
 * tab bar). "Enabled" re-syncs the token silently on every mount so a
 * re-added home-screen app replaces its dead token without a prompt.
 */
export function NotificationToggle({
  onChange,
}: {
  /** Told whenever the status changes, e.g. so a hint can hide once enabled. */
  onChange?: (status: "idle" | "enabled" | "denied" | "unsupported" | "loading") => void;
} = {}) {
  const [status, setStatus] = useState<"idle" | "enabled" | "denied" | "unsupported" | "loading">(
    () => {
      if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
      if (Notification.permission === "granted" && localStorage.getItem("push-token")) return "enabled";
      if (Notification.permission === "denied") return "denied";
      return "idle";
    },
  );

  const enableNotifications = useCallback(async (quiet = false) => {
    if (!quiet) setStatus("loading");
    const token = await requestNotificationToken();
    if (token) {
      // Register token with API (idempotent: same user+token is a no-op).
      try {
        if (token !== localStorage.getItem("push-token")) {
          await apiFetch("/push-tokens", {
            method: "POST",
            body: JSON.stringify({ token, deviceLabel: navigator.userAgent.slice(0, 100) }),
          });
          localStorage.setItem("push-token", token);
        }
        setStatus("enabled");
      } catch {
        if (!quiet) setStatus("idle");
      }
    } else if (!quiet) {
      setStatus(Notification.permission === "denied" ? "denied" : "idle");
    }
  }, []);

  // "Enabled" used to mean "a token was saved once". A re-added home-screen
  // app (or a browser that rotated the token) keeps that stale flag while
  // the server's token is dead, so re-sync silently on every open: with
  // permission already granted this prompts nothing.
  useEffect(() => {
    if (status === "enabled") void enableNotifications(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onChange?.(status);
  }, [status, onChange]);

  async function disableNotifications() {
    setStatus("loading");
    const token = localStorage.getItem("push-token");
    try {
      if (token) {
        await apiFetch("/push-tokens", {
          method: "DELETE",
          body: JSON.stringify({ token }),
        });
      }
    } catch {
      // Server may already have dropped it (stale token); still turn off here.
    }
    localStorage.removeItem("push-token");
    setStatus("idle");
  }

  if (status === "unsupported") return null;

  const on = status === "enabled";
  return (
    <button
      type="button"
      className="btn-secondary"
      role="switch"
      aria-checked={on}
      onClick={() => void (on ? disableNotifications() : enableNotifications())}
      disabled={status === "denied" || status === "loading"}
      style={{ fontSize: "0.85rem", textAlign: "left" }}
    >
      {on && "🔔 Notifications on · tap to turn off"}
      {status === "idle" && "🔕 Notifications off · tap to turn on"}
      {status === "denied" && "🔕 Notifications blocked in browser settings"}
      {status === "loading" && "Updating…"}
    </button>
  );
}
